// src/hooks/useEnrollmentsPage.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabaseClient';

/**
 * Server-side Pagination Hook for Enrollments (via rism.enrollments_list_v)
 *
 * Params:
 *   - page:      1-based page number
 *   - pageSize:  rows per page
 *   - search:    matches student_name | student_nric | subject_code | subject_name | intake_name
 *   - status:    'all' | 'Active' | 'Completed' | 'Withdrawn'
 *   - intakeId:  'all' | UUID
 *
 * Returns:
 *   {
 *     rows, count, page, pageSize, isLoading, error, refetch,
 *     softDeleteEnrollment   // sets is_deleted = true on rism.enrollments
 *   }
 *
 * Notes:
 *   - Uses `count: 'exact'` for totals & `.range(from,to)` for offset paging
 *   - Filters `is_deleted = false`
 *   - Sorts by `enrollment_date DESC, created_at DESC`
 *   - Shapes each row to match your current UI structure:
 *       row.student.full_name / nric_passport
 *       row.subject.code / name
 *       row.intake.name / start_date / end_date
 */
export function useEnrollmentsPage({
  page,
  pageSize,
  search = '',
  status = 'all',
  intakeId = 'all',
}) {
  const qc = useQueryClient();
  const from = Math.max(0, (Number(page) - 1) * Number(pageSize));
  const to = from + Number(pageSize) - 1;

  const queryKey = ['enrollments_page', { page, pageSize, search, status, intakeId }];

  const queryFn = async () => {
    const hasSearch = Boolean(search?.trim());
    const raw = (search || '').trim();
    // Light sanitization so PostgREST logic-tree isn't tripped by commas/parens
    const safe = raw.replaceAll(',', ' ').replaceAll('(', ' ').replaceAll(')', ' ').trim();

    let query = supabase
      .from('enrollments_list_v') // the view
      .select(
        `
        id,
        student_id,
        subject_id,
        intake_id,
        enrollment_date,
        status,
        created_at,
        is_deleted,
        student_name,
        student_nric,
        subject_code,
        subject_name,
        intake_name,
        intake_start_date,
        intake_end_date
      `,
        { count: 'exact' }
      )
      .eq('is_deleted', false);

    if (status && status !== 'all') {
      query = query.eq('status', status);
    }

    if (intakeId && intakeId !== 'all') {
      query = query.eq('intake_id', intakeId);
    }

    if (hasSearch) {
      const pattern = `*${safe}*`;
      // Safe OR across plain columns (no related-table paths in the logic-tree)
      query = query.or(
        [
          `student_name.ilike.${pattern}`,
          `student_nric.ilike.${pattern}`,
          `subject_code.ilike.${pattern}`,
          `subject_name.ilike.${pattern}`,
          `intake_name.ilike.${pattern}`,
        ].join(',')
      );
    }

    query = query
      .order('enrollment_date', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false })
      .range(from, to);

    const { data, count, error } = await query;
    if (error) throw error;

    // Shape rows to match your existing EnrollmentList UI props
    const rows = (data || []).map((r) => ({
      id: r.id,
      enrollment_date: r.enrollment_date,
      status: r.status,
      student_id: r.student_id,
      subject_id: r.subject_id,
      intake_id: r.intake_id,
      student: {
        id: r.student_id,
        full_name: r.student_name,
        nric_passport: r.student_nric,
      },
      subject: {
        id: r.subject_id,
        code: r.subject_code,
        name: r.subject_name,
      },
      intake: {
        id: r.intake_id,
        name: r.intake_name,
        start_date: r.intake_start_date,
        end_date: r.intake_end_date,
      },
    }));

    return { rows, count, page, pageSize };
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn,
    keepPreviousData: true,
    staleTime: 30_000,
  });

  // Soft delete (on the real table)
  const { mutateAsync: softDeleteEnrollment } = useMutation({
    mutationFn: async (id) => {
      if (!id) throw new Error('Missing enrollment id');
      const { error } = await supabase
        .from('enrollments')
        .update({ is_deleted: true })
        .eq('id', id);
      if (error) throw error;
      return { success: true };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enrollments_page'] });
    },
  });

  return {
    rows: data?.rows ?? [],
    count: data?.count ?? 0,
    page,
    pageSize,
    isLoading,
    error: error?.message || null,
    refetch,
    softDeleteEnrollment,
  };
}
